/**
 * Voice API Service
 * Handles all voice-related API calls for OpenAI Realtime API integration
 */

import { API_GATEWAY_URL, VOICE_CONFIG } from '../config/chatConfig';
import { authedFetch } from '../api/authedFetch';

class VoiceApiService {
  /**
   * Get ephemeral token for WebRTC connection
   * @param {string} authToken - User's JWT token
   * @returns {Promise<Object>} Token response with ephemeral_token, expires_at, etc.
   */
  static async getEphemeralToken() {
    try {
      // accessToken auth carried via HttpOnly cookie auto-attached by browser
      // (credentials: "include"). Backend reads from cookie or Authorization
      // header for native clients.
      const baseUrl = API_GATEWAY_URL.replace('/upload/ai', '');
      const url = `${baseUrl}/upload/realtime/token`;
      if (process.env.NODE_ENV === 'development') {
        console.log('🔗 Making token request to:', url);
      }

      const response = await authedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ model: VOICE_CONFIG.defaultModel })
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📡 Response status:', response.status, response.statusText);
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
      }

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const err = new Error(payload.message || payload.error || `Failed to get token: ${response.status}`);
        err.code = payload.error || `http_${response.status}`;
        err.resetTime = payload.reset_time;
        err.resetDate = payload.reset_date;
        err.quotaLimit = payload.quota_limit;
        err.quotaUsed = payload.quota_used;
        throw err;
      }

      const data = await response.json();
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Ephemeral token received:', {
          session_id: data.session_id,
          expires_at: data.expires_at,
          model: data.model,
          user: data.user,
          voiceUsage_percent: data.voiceUsage_percent
        });
      }

      return data;
    } catch (error) {
      console.error('❌ Failed to get ephemeral token:', error);
      throw error;
    }
  }

  /**
   * Request a voice quota increase. Backend emits a [ERR]-tagged log line
   * picked up by the existing BackendAlertsStack SNS pipeline.
   */
  static async requestVoiceAccess() {
    const baseUrl = API_GATEWAY_URL.replace('/upload/ai', '');
    const url = `${baseUrl}/upload/realtime/token`;
    const response = await authedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'requestVoiceAccess' })
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.error || `Request failed: ${response.status}`);
    }
    return response.json();
  }

  /**
   * End voice session and adjust quota based on actual usage
   * @param {string} sessionId - Voice session ID to end
   * @param {number} frontendDurationSeconds - Frontend-measured session duration in seconds
   * @returns {Promise<Object>} End session response
   */
  static async endSession(sessionId, frontendDurationSeconds = null) {
    try {
      const baseUrl = API_GATEWAY_URL.replace('/upload/ai', '');
      const url = `${baseUrl}/upload/realtime/token`;

      if (process.env.NODE_ENV === 'development') {
        console.log('🔚 Ending voice session:', sessionId);
      }

      const response = await authedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'end_session',
          session_id: sessionId,
          frontend_duration_seconds: frontendDurationSeconds
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to end session: ${response.status}`);
      }

      const data = await response.json();
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Voice session ended:', data);
      }
      return data;
    } catch (error) {
      console.error('❌ Failed to end voice session:', error);
      throw error;
    }
  }

  /**
   * Save voice transcript to database
   * @param {string} authToken - User's JWT token
   * @param {Array} transcript - Array of message objects
   * @param {string} sessionId - Voice session ID
   * @param {number} duration - Call duration in seconds
   * @returns {Promise<Object>} Save response
   */
  static async saveVoiceTranscript(transcript, sessionId, duration) {
    try {
      // API_GATEWAY_URL already contains /upload/ai
      const response = await authedFetch(API_GATEWAY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'saveMessages',
          messages: transcript
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to save transcript: ${response.status}`);
      }

      const data = await response.json();
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Voice transcript saved:', data);
      }
      return data;
    } catch (error) {
      console.error('❌ Failed to save voice transcript:', error);
      throw error;
    }
  }

  /**
   * Connect to OpenAI Realtime API via WebRTC
   * @param {string} ephemeralToken - Ephemeral token from getEphemeralToken
   * @param {string} sdpOffer - WebRTC SDP offer
   * @returns {Promise<string>} SDP answer from OpenAI
   */
  static async connectToRealtime(ephemeralToken, sdpOffer, model) {
    try {
      // The ephemeral token is bound to a specific realtime model on OpenAI's
      // side. The WebRTC connect URL MUST echo the same model string the
      // backend used when minting the token (returned in the token response).
      const target = model || VOICE_CONFIG.defaultModel;
      // GA realtime SDP exchange endpoint. OpenAI retired the beta
      // /v1/realtime?model= path (now 400) in the same gpt-realtime cutover
      // that moved token minting to /v1/realtime/client_secrets. GA SDP POST
      // is /v1/realtime/calls?model= (verified against OpenAI realtime-webrtc
      // docs 2026-06-24): Bearer <ephemeral>, Content-Type application/sdp.
      const response = await fetch(
        `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(target)}`,
        {
          method: 'POST',
          body: sdpOffer,
          headers: {
            'Authorization': `Bearer ${ephemeralToken}`,
            'Content-Type': 'application/sdp'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`OpenAI connection failed: ${response.status}`);
      }

      const sdpAnswer = await response.text();
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Connected to OpenAI Realtime API');
      }
      return sdpAnswer;
    } catch (error) {
      console.error('❌ Failed to connect to OpenAI:', error);
      throw error;
    }
  }
}

export default VoiceApiService;